import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { buildTourSteps } from "@/lib/tourSteps";

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const { user, setUser } = useAuth();
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [running, setRunning] = useState(false);
  const autoStartedRef = useRef(false);

  const finish = useCallback(async () => {
    setRunning(false);
    setStepIndex(0);
    try { await api.post("/auth/tour-seen"); } catch (_e) { void _e; }
    setUser((u) => (u ? { ...u, has_seen_tour: true } : u));
  }, [setUser]);

  const start = useCallback(async () => {
    if (!user) return;
    const built = await buildTourSteps(user);
    console.log("[tour-debug] built steps", built);
    setSteps(built);
    setStepIndex(0);
    setRunning(true);
  }, [user]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i + 1 >= steps.length) { finish(); return i; }
      return i + 1;
    });
  }, [steps, finish]);

  const prev = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);
  const skip = useCallback(() => finish(), [finish]);

  useEffect(() => {
    if (user && user.role === "CLIENT" && !user.has_seen_tour && !running && !autoStartedRef.current) {
      autoStartedRef.current = true;
      const t = setTimeout(() => start(), 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.has_seen_tour, running]);

  return (
    <TourContext.Provider value={{ steps, stepIndex, running, start, next, prev, skip }}>
      {children}
    </TourContext.Provider>
  );
}

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
};
